import { memo, PropsWithChildren, useState, useMemo, useEffect, useRef } from 'react'
import { ChevronDown, Copy, CircleCheck } from 'lucide-react'
import { Button, Tooltip } from '@highlight/ui'
import { useCollapseCodeBlocks, useLastFocusedWindow } from '../../../store'
import { useActions } from '../../../providers'
import { useTimeout } from '../../../hooks'
import { Collapsible, CollapsibleContent } from '../../radix/collapsible'
import { twMerge } from 'tailwind-merge'
import { FlowingText } from '../../FlowingText'
import ShikiHighlighter from 'react-shiki'

/**
 * Emitted together as `--shiki-light` / `--shiki-dark` CSS variables
 * (`defaultColor={false}`), so switching theme repaints instead of
 * re-highlighting. `.code-block-shiki` in styles/index.css picks one.
 */
const CODE_THEMES = { light: 'github-light', dark: 'slack-dark' } as const

interface CodeBlockProps {
  language: string
  messageId: string
  title?: string
  hideTypeInApp?: boolean
  wrapLines?: boolean
  wrapLongLines?: boolean
  maxHeight?: string
  className?: string
  truncate?: boolean
  shouldAnimate?: boolean
}

/**
 * Convert the code content to a properly formatted string:
 * - Strings are kept as-is
 * - Arrays are joined with newlines
 * - Objects are pretty-printed with JSON.stringify
 * - Other types are converted to string with a fallback
 */
const getCodeString = (content: unknown): string => {
  if (typeof content === 'string') {
    return content
  }
  if (Array.isArray(content)) {
    return content.join('\n')
  }
  if (typeof content === 'object' && content !== null) {
    try {
      return JSON.stringify(content, null, 2)
    } catch (err) {
      console.warn('Failed to stringify object:', err)
      return String(content)
    }
  }
  return String(content || '')
}

export const CodeBlock = memo(
  ({
    children,
    language,
    messageId,
    title,
    hideTypeInApp,
    wrapLines,
    wrapLongLines,
    maxHeight,
    className,
    truncate,
    shouldAnimate,
  }: PropsWithChildren<CodeBlockProps>) => {
    const { handleTyping, copyTextToClipboard } = useActions()
    const lastFocusedWindow = useLastFocusedWindow()
    const [isCopied, setCopied] = useState(false)
    const [isTyping, setTyping] = useState(false)
    const timeout = useTimeout()
    const collapseCodeBlocks = useCollapseCodeBlocks()
    const [open, setOpen] = useState(!collapseCodeBlocks)
    const [showFull, setShowFull] = useState(false)
    const code = children as string | string[]
    const headerRef = useRef<HTMLDivElement | null>(null)
    const [isHeaderVisible, setIsHeaderVisible] = useState(true)

    // Memoize the code string conversion
    const codeString = useMemo(() => getCodeString(code), [code])

    // Memoize the display code with truncation
    const displayCode = useMemo(() => {
      const shouldTruncate = truncate && codeString.length > 250
      return shouldTruncate && !showFull ? codeString.slice(0, 250) + '\n\n// Content truncated...' : codeString
    }, [codeString, truncate, showFull])

    const onCopy = () => {
      setCopied(true)
      copyTextToClipboard(codeString)
      timeout(() => setCopied(false), 2000)
      window.ampltd.trackEvent('Chat Message', { action: 'Copy Code', language })
    }

    const updateLastCodeBlockInsertButton = () => {
      const codeBlockInsertButtons = document.querySelectorAll('.code-block-insert-button')
      codeBlockInsertButtons.forEach((button) => (button.innerHTML = ''))
      const lastButton = codeBlockInsertButtons[codeBlockInsertButtons.length - 1]
      if (lastButton) {
        lastButton.innerHTML = '⇧TAB'
      }
    }

    const onHandleTyping = async () => {
      if (isTyping || !handleTyping || !lastFocusedWindow) return
      setTyping(true)
      await handleTyping(
        {
          content: codeString,
          id: `code_block_${messageId}`,
          role: 'assistant',
          conversation_id: `code_block_${messageId}`,
          given_feedback: null,
        },
        lastFocusedWindow,
        { source: 'code_block' },
      )
      setTyping(false)
      setTimeout(updateLastCodeBlockInsertButton, 50)
      window.ampltd.trackEvent('Chat Message', { action: 'Type Code', language })
    }

    // Update open state when collapseCodeBlocks setting changes
    useEffect(() => {
      setOpen(!collapseCodeBlocks)
    }, [collapseCodeBlocks])

    useEffect(() => {
      updateLastCodeBlockInsertButton()
    }, [])

    useEffect(() => {
      const el = headerRef.current
      if (!el || typeof IntersectionObserver === 'undefined') return
      const io = new IntersectionObserver(([entry]) => setIsHeaderVisible(entry.isIntersecting), {
        root: null,
        threshold: 0.1,
      })
      io.observe(el)
      return () => io.disconnect()
    }, [open])

    if (language === 'plaintext') {
      return (
        <PlaintextBlock messageId={messageId} title={title} shouldAnimate={shouldAnimate}>
          {children}
        </PlaintextBlock>
      )
    }

    const Actions = (
      <div className="flex items-center gap-2">
        <Tooltip.Provider>
          <Tooltip>
            <Tooltip.Trigger render={<Button size="xs" shape="circle" variant="ghost" onClick={onCopy} />}>
              {isCopied ? <CircleCheck size={18} /> : <Copy size={18} />}
            </Tooltip.Trigger>
            <Tooltip.Content>{isCopied ? 'Copied' : 'Copy'}</Tooltip.Content>
          </Tooltip>
          {typeof handleTyping === 'function' && lastFocusedWindow && !hideTypeInApp && (
            <Tooltip>
              <Tooltip.Trigger render={<div className="relative flex" />}>
                <Button
                  size="xs"
                  shape="rounded"
                  variant="ghost"
                  onClick={onHandleTyping}
                  disabled={isTyping}
                  className="gap-1 px-1.5 py-1"
                >
                  <img src={lastFocusedWindow.appIcon} className="h-4 w-4" alt={`${lastFocusedWindow.appName} icon`} />
                  <span>{isTyping ? 'Inserting' : 'Insert'}</span>
                  <span className="code-block-insert-button text-xs opacity-30"></span>
                </Button>
              </Tooltip.Trigger>
              <Tooltip.Content>{`Type into ${lastFocusedWindow.appName}`}</Tooltip.Content>
            </Tooltip>
          )}
          <Tooltip>
            <Tooltip.Trigger
              render={<Button size="xs" shape="circle" variant="ghost" onClick={() => setOpen(!open)} />}
            >
              <span className="sr-only">{open ? 'Hide' : 'Show'}</span>
              <ChevronDown size={18} className={twMerge('transition-transform', open && 'rotate-180')} />
            </Tooltip.Trigger>
            <Tooltip.Content>{open ? 'Hide' : 'Show'}</Tooltip.Content>
          </Tooltip>
        </Tooltip.Provider>
      </div>
    )

    return (
      <Collapsible open={open} onOpenChange={setOpen} data-md-block>
        <div
          ref={headerRef}
          className={twMerge(
            'bg-element text-foreground-placeholder border-border flex items-center justify-between rounded-t-2xl border px-3 py-2 pe-2 text-xs font-normal',
            !open && 'rounded-b-2xl',
          )}
        >
          <span className="text-foreground">{title ?? language}</span>
          {Actions}
        </div>

        <CollapsibleContent
          className={twMerge(
            'border-border bg-element gap-0 overflow-hidden rounded-b-2xl border-b border-l border-r',
            className,
          )}
        >
          <div className="relative w-full">
            <ShikiHighlighter
              language={language}
              theme={CODE_THEMES}
              defaultColor={false}
              addDefaultStyles={false}
              showLanguage={false}
              // The wrapper defaults to another <pre>, which `.markdown pre`
              // (markdown-content.css) draws as a second card.
              as="div"
              className="code-block-shiki"
              delay={150}
            >
              {displayCode}
            </ShikiHighlighter>
            {!truncate && open && displayCode.split('\n').length > 5 && !isHeaderVisible && (
              <div className="absolute bottom-4 right-4">{Actions}</div>
            )}
            {truncate && codeString.length > 250 && (
              <div className="border-border flex justify-center border-t p-2">
                <Button size="xs" shape="rounded" variant="ghost" onClick={() => setShowFull(!showFull)}>
                  {showFull ? 'Show less' : 'Show full content'}
                </Button>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    )
  },
)

const PlaintextBlock = ({
  children,
  messageId,
  title,
  shouldAnimate,
}: PropsWithChildren<{ messageId: string; title?: string; shouldAnimate?: boolean }>) => {
  const { handleTyping, copyTextToClipboard } = useActions()
  const lastFocusedWindow = useLastFocusedWindow()
  const [isTyping, setTyping] = useState(false)
  const [isCopied, setCopied] = useState(false)
  const timeout = useTimeout()

  // Memoize the code string conversion
  const codeString = useMemo(() => getCodeString(children as string | string[]), [children])

  const onCopy = () => {
    setCopied(true)
    copyTextToClipboard(codeString)
    timeout(() => setCopied(false), 2000)
    window.ampltd.trackEvent('Chat Message', { action: 'Copy Plaintext' })
  }

  const onHandleTyping = async () => {
    if (isTyping || !handleTyping || !lastFocusedWindow) return
    setTyping(true)
    await handleTyping(
      {
        content: codeString,
        id: `code_block_${messageId}`,
        role: 'assistant',
        conversation_id: `code_block_${messageId}`,
        given_feedback: null,
      },
      lastFocusedWindow,
      { source: 'code_block' },
    )
    setTyping(false)
    window.ampltd.trackEvent('Chat Message', { action: 'Type Plaintext' })
  }

  return (
    <div className="border-border bg-element my-2 rounded-2xl border">
      <div
        className={twMerge(
          'text-foreground-placeholder border-border flex items-center justify-between rounded-t-2xl border-b px-3 py-2 pe-2 text-xs font-normal',
        )}
      >
        <span className="text-foreground text-[13px] font-normal" style={{ fontFamily: "'Inter', sans-serif" }}>
          {title ?? 'Text'}
        </span>
        <div className="flex items-center gap-2">
          <Tooltip.Provider>
            <Tooltip>
              <Tooltip.Trigger render={<Button size="xs" shape="circle" variant="ghost" onClick={onCopy} />}>
                {isCopied ? <CircleCheck size={18} /> : <Copy size={18} />}
              </Tooltip.Trigger>
              <Tooltip.Content>{isCopied ? 'Copied' : 'Copy'}</Tooltip.Content>
            </Tooltip>
            {handleTyping && lastFocusedWindow && (
              <Tooltip>
                <Tooltip.Trigger render={<div className="relative flex" />}>
                  <Button
                    size="xs"
                    shape="rounded"
                    variant="ghost"
                    onClick={onHandleTyping}
                    disabled={isTyping}
                    className="gap-1 px-1.5 py-1"
                  >
                    <img
                      src={lastFocusedWindow.appIcon}
                      className="h-4 w-4"
                      alt={`${lastFocusedWindow.appName} icon`}
                    />
                    <span>{isTyping ? 'Inserting' : 'Insert'}</span>
                  </Button>
                </Tooltip.Trigger>
                <Tooltip.Content>{`Type into ${lastFocusedWindow.appName}`}</Tooltip.Content>
              </Tooltip>
            )}
          </Tooltip.Provider>
        </div>
      </div>
      <div className="px-3 py-3">
        <div style={{ fontFamily: "'Inter', sans-serif" }}>
          <FlowingText text={codeString} shouldAnimate={shouldAnimate} className="m-0 whitespace-pre-wrap text-sm" />
        </div>
      </div>
    </div>
  )
}
